/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  ButtonBase,
  SelectChangeEvent,
  Stack,
  styled,
  useTheme,
} from '@mui/material';
import React, { SetStateAction, useEffect, useMemo, useRef, useState } from 'react';
import _ from 'lodash';
import { colors } from '@farmlands/mui-theme';
import { useSelector } from 'react-redux';

import Typography from '../../../../components/Typography';
import MaterialSymbol from '../../../../components/MaterialSymbol';
import Input from '../../../../components/Input';
import { useCampaignContext } from './CampaignContext';
import { formatPrice } from '../../../../util/helpers';
import {
  DeliveryMonthsPlan,
  MonthlyOrder,
  SaleAgreementPromo,
  SalesAgreementPromoProductSelectionVariant,
} from './types';
import { ModalActions } from '../../../../components/Modal';
import { Button } from '../../../../components/Button';
import TestIds from '../../../../testIds';
import {
  calculateTotalFreightCost,
  getMinMonthPeriods,
  getPrice,
  selectProductDeliveryMonthPlan,
} from './utils';
import dayjs from 'dayjs';
import TabHeader from './TabHeader';
import { EditButton } from '../../../products/components/ProductCard/EditButton';
import {
  EditSalesAgreementProductPriceModal,
  IEditedSAItemDetails,
} from './EditSalesAgreementProductModal';
import AdditivesOption, { feedRates } from './AdditivesOption';
import DropdownList, { DropdownItem } from '../../../../components/Dropdownlist';
import { UNIT_MAP_LONG, UNIT_MAP_PLURAL } from '../../../../util/consts';
import ProductDetailModal, { useProductDetailModalControl } from './ProductDetailModal';
import PrintVariants from '../../../products/components/ProductCard/PrintVariants';
import { selectCurrentFarmerAccount } from '../../../azure-b2c/storage';

export const getTotalUnits = (
  products: SalesAgreementPromoProductSelectionVariant[],
  id: number,
): number => {
  const orders = products.find((product) => product.productId === id)?.orders;
  return orders?.reduce((sum, order) => sum + order.quantity, 0) ?? 0;
};
interface QuantityTabProps {
  config: SaleAgreementPromo;
}

export const QuantityTab: React.FC<QuantityTabProps> = ({ config }) => {
  const { state, setState } = useCampaignContext();
  const [error, setError] = useState<Record<string, QuantityErrorMap>>({});
  const [touched, setTouched] = useState(false);
  const isFreightInclusive = !config.freightRequired;

  useEffect(() => {
    if (!state.productSelected) return;

    const newErrorState: Record<string, QuantityErrorMap> = state.productSelected.reduce(
      (acc: Record<string, QuantityErrorMap>, product) => {
        const deliveryMonthPlan = product.selectedDeliveryMonthPlan;
        const totalOrders = product.orders?.reduce((sum, order) => sum + order.quantity, 0) ?? 0;
        const totalMinError =
          totalOrders < (deliveryMonthPlan?.minTotalQuantity ?? product.quantity);

        const orderMonths = product?.orders?.filter((order) => order.quantity > 0).length ?? 0;

        const monthMinOrderError = product.orders?.map((order) => {
          return (
            order.quantity !== 0 &&
            (order.quantity < (product.minReleaseQuantity ?? 0) ||
              order.quantity > (product.maxReleaseQuantity ?? 99999999999999))
          );
        });

        const minMonthPeriods = getMinMonthPeriods(config.minPeriods, deliveryMonthPlan);
        const minMonthError = orderMonths < (minMonthPeriods ?? 0);

        acc[product.productId] = {
          minMonth: minMonthError,
          totalMin: totalMinError,
          monthMinOrder: monthMinOrderError,
          priceError: product.unitPrice <= 0 && (product.editedPrice ?? 0) <= 0,
        };
        return acc;
      },
      {} as Record<string, QuantityErrorMap>,
    );
    setError(newErrorState);
  }, [state.productSelected, config.minPeriods]);

  const [editPriceOpen, setEditPriceOpen] = React.useState(false);
  const [editProduct, setEditProduct] =
    React.useState<SalesAgreementPromoProductSelectionVariant>();

  const handleOpenEditPriceModal = (product: SalesAgreementPromoProductSelectionVariant) => {
    setEditProduct(product);
    setEditPriceOpen(true);
  };

  const onApplyEditedPrice = ({
    editedPrice,
    editedPriceReasonCode,
    editedPriceReasonNote,
    item,
  }: IEditedSAItemDetails) => {
    const updatedProducts: SalesAgreementPromoProductSelectionVariant[] = state.productSelected.map(
      (product: SalesAgreementPromoProductSelectionVariant) => {
        if (product.productId === item.productId) {
          return {
            ...product,
            editedPrice,
            lastEditedReasonCode: editedPriceReasonCode,
            lastEditedReasonNote: editedPriceReasonNote,
          };
        }

        return product;
      },
    );

    setState({
      productSelected: updatedProducts,
    });
  };

  const onRevertEditedPrice = (item: SalesAgreementPromoProductSelectionVariant): void => {
    const updatedProducts: SalesAgreementPromoProductSelectionVariant[] = state.productSelected.map(
      (product: SalesAgreementPromoProductSelectionVariant) => {
        if (product.productId === item.productId) {
          return {
            ...product,
            editedPrice: undefined,
            lastEditedReasonCode: undefined,
            lastEditedReasonNote: undefined,
          };
        }

        return product;
      },
    );

    setState({
      productSelected: updatedProducts,
    });
  };

  return (
    <Stack
      direction='column'
      gap={'1rem'}
      pb='100px'
      data-testid={TestIds.feat.agreement.campaignModal.quantityTab.component}
    >
      <EditSalesAgreementProductPriceModal
        open={editPriceOpen}
        setOpen={setEditPriceOpen}
        onApplyEditedPrice={onApplyEditedPrice}
        onRevertEditedPrice={onRevertEditedPrice}
        item={editProduct}
      />
      <TabHeader
        title='Delivery plan'
        caption='Enter quantity required each month for every product selected'
      />
      {state.productSelected.map((prod) => {
        const handleEditPriceCallback = config.editPriceAllowed
          ? () => {
              handleOpenEditPriceModal(prod);
            }
          : undefined;

        const summary: AccordionItemSummary[] = [
          {
            type: 'unit',
            title: prod.unit,
            calculation: prod.orders ? prod.orders.reduce((a, b) => a + b.quantity, 0) : 0,
          },
          {
            type: 'price',
            title: 'Total',
            calculation: getPrice(prod) * getTotalUnits(state.productSelected, prod.productId),
          },
        ];

        if (!isFreightInclusive) {
          summary.push({
            type: 'price',
            title: 'Freight',
            calculation: calculateTotalFreightCost(prod, state.freightCharge),
          });
        }

        return (
          <SalesAccordionItem
            sizeDescription={prod.sizeDescription}
            colourDescription={prod.colourDescription}
            config={config}
            allowRemoveProduct
            error={error[prod.productId]}
            key={prod.productId}
            productId={prod.productId}
            itemNumber={prod.itemNumber}
            unit={prod.unit}
            minQuantity={prod.selectedDeliveryMonthPlan?.minTotalQuantity ?? prod.quantity}
            touched={touched}
            setTouched={setTouched}
            title={prod.description}
            deliveryMonthPlans={prod.deliveryMonthPlans}
            selectedDeliveryMonthPlan={prod.selectedDeliveryMonthPlan}
            defaultExpanded
            handleOpenEditPriceModal={handleEditPriceCallback}
            summary={summary}
          />
        );
      })}

      <ModalActions showDivider>
        <Button
          variant='contained'
          // disabled
          onClick={() => {
            if (!error) return;
            const value = Object.values(error);

            const minMonthError = value.map((v) => v.minMonth);
            const totalMinError = value.map((v) => v.totalMin);
            const monthMinOrderError = value.flatMap((v) => v.monthMinOrder?.map((value) => value));
            const priceError = value.map((v) => v.priceError);

            if (
              !minMonthError.includes(true) &&
              !totalMinError.includes(true) &&
              !monthMinOrderError.includes(true) &&
              !priceError.includes(true)
            ) {
              setState({ tabIndex: 3 });
            }
            setTouched(true);
          }}
          // data-testid={TestIds.feat.product.details.orderButton}
          sx={(theme) => ({
            [theme.breakpoints.down('sm')]: {
              width: '100%',
            },
          })}
        >
          Next Step
        </Button>
      </ModalActions>
    </Stack>
  );
};

export interface AccordionItemSummary {
  title: string;
  type: 'price' | 'unit';
  calculation?: number;
}
export interface QuantityErrorMap {
  minMonth: boolean;
  totalMin: boolean;
  monthMinOrder?: boolean[];
  priceError: boolean;
}
interface SalesAccordionItemProps {
  title: string;
  config?: SaleAgreementPromo;
  summary?: AccordionItemSummary[];
  minQuantity: number;
  error?: QuantityErrorMap; // Simplified to boolean per product
  touched?: boolean;
  unit: string;
  setTouched?: React.Dispatch<SetStateAction<boolean>>;
  productId: number;
  disabled?: boolean; // disabled accordian toggle
  allowRemoveProduct?: boolean;
  editable?: boolean; // disabled input edit
  defaultExpanded?: boolean; // default value of expand
  handleOpenEditPriceModal?: () => void;
  deliveryMonthPlans: DeliveryMonthsPlan[];
  selectedDeliveryMonthPlan?: DeliveryMonthsPlan;
  sizeDescription?: string | null;
  colourDescription?: string | null;
  itemNumber: string;
}

const StyledAccordion = styled(Accordion)(({ theme }) => ({
  boxShadow: 'none',
  background: 'white',
  cursor: 'default',
  border: `1px solid ${theme.colors.grey400}`,
  borderRadius: '0 !important',
  '&.Mui-expanded': {
    margin: '0 !important',
  },
}));

const StyledAccordionSummary = styled(AccordionSummary)({
  padding: '1rem',
  cursor: 'defualt',
  '& .MuiAccordionSummary-content': {
    margin: '0 !important',
  },
});

const StyledAccordionDetails = styled(AccordionDetails)(({ theme }) => ({
  cursor: 'default',
  borderTop: `1px solid ${theme.colors.grey400}`,
  paddingX: '1rem',
  paddingTop: '1rem',
  paddingBottom: '1.5rem',
  backgroundColor: theme.colors.primary50,
}));

const StyledExpandButton = styled(ButtonBase)({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '4px',
});

const DivGrid = styled('div')(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(3, minmax(0,1fr))',
  [theme.breakpoints.up('sm')]: {
    gridTemplateColumns: 'repeat(4, minmax(0,1fr))',
  },
  [theme.breakpoints.up('md')]: {
    gridTemplateColumns: 'repeat(7, minmax(0,1fr))',
  },

  columnGap: '2rem',
  rowGap: '1rem',
}));

export const SalesAccordionItem: React.FC<SalesAccordionItemProps> = ({
  title,
  sizeDescription,
  colourDescription,
  config,
  summary,
  minQuantity,
  productId,
  unit,
  itemNumber,
  disabled,
  error,
  setTouched,
  touched,
  allowRemoveProduct,
  editable = true,
  defaultExpanded = false,
  handleOpenEditPriceModal,
  deliveryMonthPlans,
  selectedDeliveryMonthPlan,
}) => {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const theme = useTheme();
  const { setState, state } = useCampaignContext();
  const currentProduct = state.productSelected.find((a) => a.productId === productId);
  const otherProduct = state.productSelected.filter((a) => a.productId !== productId);
  const unitLabel = UNIT_MAP_LONG[unit as keyof typeof UNIT_MAP_LONG];
  const {
    setOpen,
    isOpen,
    setClose,
    productId: productSelectedId,
  } = useProductDetailModalControl();
  const account = useSelector(selectCurrentFarmerAccount);

  const deliveryMonthPlansDropdownItems: DropdownItem[] = deliveryMonthPlans.map((dmp) => {
    if (dmp.unitPrice > 0 || (account?.hasTfoAccess && config?.editPriceAllowed)) {
      return {
        label: `${dmp.numOfMonths} months - ${formatPrice(dmp.unitPrice)} per ${unitLabel}`,
        value: String(dmp.numOfMonths),
      };
    } else {
      return {
        label: `${dmp.numOfMonths} months - Contact your Farmlands Representative`,
        value: String(dmp.numOfMonths),
        disabled: true,
      };
    }
  });

  const renderSummaryBlocks = useMemo(() => {
    const hasLabel = Object.keys(UNIT_MAP_PLURAL).includes(unit);

    if (!summary) return;
    if (summary)
      return summary.map((item) => {
        if (item.type === 'unit') {
          const totalUnit = getTotalUnits(state.productSelected, productId);
          return (
            <Stack direction='column' gap='4px' key={`${item.title}-${item.type}`}>
              <Input
                onFocus={(e) => e.currentTarget.select()}
                sx={{
                  WebkitTextFillColor: 'black',
                  color: 'black',
                  backgroundColor: theme.colors.grey100,

                  minWidth: '84px',
                  maxWidth: '84px',
                  minHeight: '45px',
                  maxHeight: '45px',
                }}
                disabled
                label={hasLabel ? UNIT_MAP_PLURAL[unit as keyof typeof UNIT_MAP_PLURAL] : unit}
                testId={TestIds.feat.agreement.magnesium.quantityTab.totalUnit}
                disableError
                value={totalUnit === 0 ? 0 : _.trimStart(String(totalUnit), '0')}
                type='number'
              />
            </Stack>
          );
        }

        if (item.type === 'price') {
          return (
            <Stack direction='column' gap='4px' key={`${item.title}-${item.type}`}>
              <Typography variant='body2' sx={{ textTransform: 'capitalize' }} fontWeight={700}>
                {item.title}
              </Typography>
              <Stack direction='column'>
                <Typography
                  lineHeight={'27px'}
                  variant='body1'
                  color='secondary700'
                  fontWeight={700}
                >
                  {formatPrice(item.calculation ?? 0)}
                </Typography>
                <Typography fontSize='12px' lineHeight={'18px'} color='secondary700'>
                  GST excl.
                </Typography>
              </Stack>

              {handleOpenEditPriceModal && item.title === 'Total' && (
                <EditButton onClick={handleOpenEditPriceModal} />
              )}
            </Stack>
          );
        }
      });
  }, [summary, setState]);

  const toggleExpand = () => {
    if (!disabled) {
      setExpanded((prev) => !prev);
    }
  };

  const expandButtonLabel = () => {
    if (editable) {
      return 'Expand to edit details';
    }
    if (expanded) {
      return 'Collapse';
    }
    return 'Expand';
  };

  const renderActions = () => {
    const sx = {
      padding: '0rem 0',
    };

    if (allowRemoveProduct) {
      return (
        <Button
          disableRipple
          onClick={() => {
            if (otherProduct.length === 0) {
              setState({
                tabIndex: 1,
                productSelected: [...otherProduct],
              });
            } else
              setState({
                productSelected: [...otherProduct],
              });
          }}
          variant='text'
          sx={sx}
        >
          <Typography variant='caption'>Remove</Typography>
        </Button>
      );
    }

    if (!editable) {
      return (
        <Button
          disableRipple
          onClick={() => {
            setState({
              tabIndex: 2,
            });
          }}
          variant='text'
          sx={sx}
        >
          <Typography variant='caption'>Edit</Typography>
        </Button>
      );
    }
  };

  const handlePlanChange = (event: SelectChangeEvent<string>) => {
    const selectedPlan = currentProduct?.deliveryMonthPlans.find(
      (dmp) => dmp.numOfMonths === Number(event.target.value),
    );

    if (selectedPlan) {
      setState({
        productSelected: state.productSelected.map((prodSelected) => {
          if (prodSelected.productId === productId) {
            return selectProductDeliveryMonthPlan(prodSelected, selectedPlan);
          }
          return prodSelected;
        }),
      });
    }
  };

  const renderDeliveryMonthPlans = () => {
    const isSinglePlan = deliveryMonthPlansDropdownItems.length === 1 || !editable;
    const dropdownStyleRemoveSx = {
      backgroundColor: 'transparent',
      color: colors.grey800,
      padding: 0,
      '&.Mui-disabled': {
        color: colors.grey800,
        backgroundColor: 'transparent',
      },
      '& .Mui-disabled': {
        WebkitTextFillColor: `${colors.grey800} !important`,
      },
      '&:hover fieldset': {
        border: 'none',
      },
      '& fieldset': {
        border: 'none',
      },
      '& .MuiSelect-icon': {
        display: 'none',
      },
    };

    return (
      <DropdownList
        items={deliveryMonthPlansDropdownItems}
        label='Planned delivery months'
        value={
          selectedDeliveryMonthPlan?.numOfMonths
            ? String(selectedDeliveryMonthPlan?.numOfMonths)
            : ''
        }
        onChange={handlePlanChange}
        testId='TODO:test'
        disabled={!editable || isSinglePlan}
        sx={{
          marginBottom: '1rem',
          ...(isSinglePlan ? dropdownStyleRemoveSx : {}),
        }}
      />
    );
  };

  const setEnable = (checked: boolean, product: SalesAgreementPromoProductSelectionVariant) => {
    // Find product that matches and set new state of that product
    const updatedProduct = {
      ...product,
      feedRate: checked ? feedRates[0] : undefined,
    };
    setState({
      productSelected: [
        ...state.productSelected.filter((p) => p.productId !== product.productId),
        updatedProduct,
      ],
    });
  };

  const setFeedRate = (value: string, product: SalesAgreementPromoProductSelectionVariant) => {
    setState({
      productSelected: [
        ...state.productSelected.filter((p) => p.productId !== product.productId),
        {
          ...product,
          feedRate: feedRates.find((item) => item.value === value),
        },
      ],
    });
  };

  const setAdditives = (value: string, product: SalesAgreementPromoProductSelectionVariant) => {
    setState({
      productSelected: [
        ...state.productSelected.filter((p) => p.productId !== product.productId),
        {
          ...product,
          additives: value,
        },
      ],
    });
  };

  const variants = [
    ...(sizeDescription ? [{ label: 'Size', value: sizeDescription }] : []),
    ...(colourDescription ? [{ label: 'Colour', value: colourDescription }] : []),
  ];
  const minMonthPeriods = getMinMonthPeriods(config?.minPeriods, selectedDeliveryMonthPlan);

  return (
    <StyledAccordion expanded={expanded}>
      <StyledAccordionSummary sx={{ cursor: 'default' }}>
        <Stack direction='column' gap='1rem' width='100%' sx={{ cursor: 'default' }}>
          <Stack direction='column' gap={1} sx={{ cursor: 'default' }}>
            <Stack
              direction='row'
              width='100%'
              justifyContent={'space-between'}
              gap={2}
              alignItems='start'
              sx={{ cursor: 'default' }}
            >
              <Typography
                variant='h4'
                onClick={() => setOpen(Number(productId))}
                sx={{ textDecoration: 'underline', cursor: 'pointer' }}
              >
                {title}
              </Typography>
              {renderActions()}
            </Stack>
            <PrintVariants items={variants} direction='row' />
            <Typography variant='caption' color='grey700'>
              SKU {itemNumber}
            </Typography>
          </Stack>
          <Stack direction='column'>
            <Stack direction='row' alignItems='start' gap='1rem'>
              {renderSummaryBlocks}
            </Stack>
            {error?.totalMin && touched ? (
              <Typography variant='body2' color='error700' fontStyle={'italic'} alignSelf={'end'}>
                Minimum {minQuantity} total {unitLabel}
              </Typography>
            ) : null}

            {error?.minMonth && touched ? (
              <Typography variant='body2' color='error700' fontStyle={'italic'} alignSelf={'end'}>
                Minimum {minMonthPeriods} months to be fulfilled
              </Typography>
            ) : null}
            {error?.monthMinOrder?.includes(true) && touched ? (
              <Typography variant='body2' color='error700' fontStyle={'italic'} alignSelf={'end'}>
                Minimum {currentProduct?.minReleaseQuantity ?? 0}
                {currentProduct?.maxReleaseQuantity
                  ? `, Maximum ${currentProduct.maxReleaseQuantity} per populated month`
                  : ''}
              </Typography>
            ) : null}
            {error?.priceError && touched ? (
              <Typography variant='body2' color='error700' fontStyle={'italic'} alignSelf={'end'}>
                Price not set
              </Typography>
            ) : null}
          </Stack>

          {currentProduct?.feedRate && (
            <Stack
              gap={1}
              direction='row'
              sx={{
                backgroundColor: theme.colors.secondary50,
                color: theme.colors.secondary700,
                borderRadius: '8px',
                position: 'relative',
                maxWidth: '250px',
                padding: '0.5rem',
              }}
            >
              <MaterialSymbol>info</MaterialSymbol>
              <Typography mt={'1px'} variant='body2' color='shade100'>
                Additives added
              </Typography>
            </Stack>
          )}

          <StyledExpandButton disableRipple onClick={toggleExpand}>
            <Typography variant='caption'>{expandButtonLabel()}</Typography>
            <MaterialSymbol
              sx={{
                transitionDuration: '300ms',
                fontSize: '1.25rem',
                transform: expanded ? 'rotate(0deg)' : 'rotate(180deg)',
              }}
            >
              keyboard_arrow_up
            </MaterialSymbol>
          </StyledExpandButton>
        </Stack>
      </StyledAccordionSummary>
      <StyledAccordionDetails>
        {renderDeliveryMonthPlans()}
        <DivGrid>
          {currentProduct?.orders?.map((order, index) => {
            const currentMonth = dayjs().month(order.month).year(order.year);
            let label = `${currentMonth.format('MMM')}`;
            if (
              index === 0 ||
              (currentProduct.orders &&
                order.year > currentProduct.orders[0].year &&
                order.year > currentProduct.orders[index - 1].year)
            ) {
              label = `${currentMonth.format('MMM-YYYY')}`;
            }
            let disabled = false;
            if (dayjs().isAfter(currentMonth, 'month')) {
              disabled = true;
            }
            return (
              <UnscrollableNumberInput
                key={`${order.month}/${order.year}`}
                disabled={!editable || disabled}
                setTouched={setTouched}
                order={order}
                otherProducts={otherProduct}
                monthIndex={index}
                currentProduct={currentProduct}
                error={error}
                label={label}
              />
            );
          })}
        </DivGrid>
        {currentProduct && config?.showAdditives && (
          <AdditivesOption
            product={currentProduct}
            editable={editable}
            setEnable={(e) => setEnable(e, currentProduct)}
            setFeedRate={(e) => setFeedRate(e, currentProduct)}
            setAdditives={(e) => setAdditives(e, currentProduct)}
          />
        )}
      </StyledAccordionDetails>
      <ProductDetailModal open={isOpen} productId={productSelectedId} handleClose={setClose} />
    </StyledAccordion>
  );
};

interface UnscrollableNumberInputProps {
  disabled?: boolean;
  setTouched?: (value: boolean) => void;
  currentProduct: SalesAgreementPromoProductSelectionVariant;
  order: MonthlyOrder;
  otherProducts: SalesAgreementPromoProductSelectionVariant[];
  error:
    | {
        minMonth: boolean;
        totalMin: boolean;
        monthMinOrder?: boolean[];
      }
    | undefined;
  monthIndex: number;
  label: string;
}

const UnscrollableNumberInput: React.FC<UnscrollableNumberInputProps> = ({
  monthIndex,
  disabled,
  setTouched,
  currentProduct,
  order,
  otherProducts,
  label,
  error,
}) => {
  const { setState } = useCampaignContext();
  const ref = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!ref.current) return;
    const handleWheel = (e: WheelEvent) => {
      e.preventDefault();
    };
    ref.current.addEventListener('wheel', handleWheel);

    return () => {
      if (!ref.current) return;
      ref.current.removeEventListener('wheel', handleWheel);
    };
  }, []);
  return (
    <Input
      ref={ref}
      disabled={disabled}
      // error={order.quantity < minQuantity}
      disableError
      onPaste={(e) => {
        const pasteData = e.clipboardData.getData('Text');
        // Allow only digits
        if (!/^\d*$/.test(pasteData)) {
          e.preventDefault();
        }
      }}
      onFocus={(e) => e.currentTarget.select()}
      onKeyDown={(e) => {
        if (e.key === 'e' || e.key === '+' || e.key === '-' || e.key === '.') e.preventDefault();
      }}
      onChange={(e) => {
        setTouched?.(true);

        const value = Math.max(0, Number(e.target.value));
        const updatedOrders = currentProduct.orders?.map((o) =>
          o.year === order.year && o.month === order.month ? { ...o, quantity: value } : o,
        );

        if (!updatedOrders) return;

        const updatedProduct = {
          ...currentProduct,
          orders: updatedOrders,
        };

        setState({
          productSelected: [...otherProducts, updatedProduct],
        });
      }}
      error={error?.monthMinOrder?.[monthIndex]}
      label={label}
      testId=''
      type='number'
      value={order.quantity === 0 ? 0 : _.trimStart(String(order.quantity), '0')}
      sx={{
        '&.Mui-disabled': {
          backgroundColor: colors.grey100,
        },
      }}
    />
  );
};
